import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Grid,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Box,
  Typography,
  Checkbox,
  Chip,
  SelectChangeEvent,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import CategoryIcon from '@mui/icons-material/Category';
import { RestaurantMenu as RecipeIcon, MenuBook as MenuIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { InventoryItem, Warehouse, InventoryCategory } from '../types/inventory.types';
import { accountingApi } from '../../../../config/apiClient';

/**
 * Gelişmiş ürün seçim modalı (muhasebe/stok tarafı).
 *
 * QuickSaleProductSelectionModal (hızlı satış → depo/stok tabı) ile aynı UX:
 * arama + depo/kategori filtresi + server-side sayfalama + çoklu seçim.
 * Binlerce ürün olan şirketlerde Autocomplete yerine bu modal kullanılır
 * (RecipeModal ve ProductMenuModal ürün ekleme akışları).
 */
interface InventoryProductSelectionModalProps {
  open: boolean;
  onClose: () => void;
  /** Modal başlığı (ör: "Malzeme Seç", "Menüye Ürün Seç") */
  title: string;
  warehouses: Warehouse[];
  categories: InventoryCategory[];
  /** Listede gösterilmeyecek ürünler (zaten ekli olanlar + ürünün kendisi) */
  excludeItemIds?: string[];
  /** true ise hasMenu=true olan ürünler gizlenir (menü içine menü eklenemez) */
  excludeMenuItems?: boolean;
  /** Seçim onaylandığında tam ürün bilgileri ile çağrılır */
  onSelectProducts: (selectedProducts: InventoryItem[]) => void;
}

const InventoryProductSelectionModal: React.FC<InventoryProductSelectionModalProps> = ({
  open,
  onClose,
  title,
  warehouses,
  categories,
  excludeItemIds = [],
  excludeMenuItems = false,
  onSelectProducts,
}) => {
  const { i18n } = useTranslation();
  // startsWith: "tr-TR" gibi bölgeli kodlarda da doğru çalışsın (bkz. src/i18n/index.ts)
  const isTr = !(i18n.language || 'tr').toLowerCase().startsWith('en');
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);
  const localeCode = isTr ? 'tr-TR' : 'en-US';

  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<string | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [loading, setLoading] = useState(false);
  // Sayfalar arası seçim koruması: id → tam ürün bilgisi
  const [selectedMap, setSelectedMap] = useState<Map<string, InventoryItem>>(new Map());
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Modal açıldığında formu sıfırla
  useEffect(() => {
    if (open) {
      setSearchInput('');
      setSearchTerm('');
      setSelectedWarehouseId(null);
      setSelectedCategoryId(null);
      setPage(0);
      setSelectedMap(new Map());
    }
  }, [open]);

  // Debounced arama (300ms)
  const handleSearchInputChange = (value: string) => {
    setSearchInput(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setSearchTerm(value);
      setPage(0);
    }, 300);
  };

  // Server-side pagination ile ürünleri yükle
  useEffect(() => {
    if (!open) return;

    const loadProducts = async () => {
      setLoading(true);
      try {
        const response = await accountingApi.post(`/api/Inventory/items/paged`, {
          pageNumber: page + 1,
          pageSize: rowsPerPage,
          searchTerm: searchTerm || undefined,
          warehouseId: selectedWarehouseId || undefined,
          categoryId: selectedCategoryId || undefined,
          isActive: true,
        });

        const data = response.data;
        const itemsArray = data.items || data.Items || [];
        const total = data.totalCount || data.TotalCount || 0;

        const mappedItems: InventoryItem[] = itemsArray.map((apiItem: any) => ({
          id: apiItem.id || apiItem.Id || '',
          name: apiItem.name || apiItem.Name || '',
          stockCode: apiItem.stockCode || apiItem.StockCode || '',
          barcode: apiItem.barcode || apiItem.Barcode || '',
          unit: apiItem.unit || apiItem.Unit || 'adet',
          salePrice: apiItem.salePrice || apiItem.SalePrice || 0,
          purchasePrice: apiItem.purchasePrice || apiItem.PurchasePrice || 0,
          purchaseCurrency: apiItem.purchaseCurrency || apiItem.PurchaseCurrency || 'TRY',
          canBeNegative: apiItem.canBeNegative ?? apiItem.CanBeNegative ?? true,
          isActive: apiItem.isActive ?? apiItem.IsActive ?? true,
          hasRecipe: apiItem.hasRecipe ?? apiItem.HasRecipe ?? false,
          hasMenu: apiItem.hasMenu ?? apiItem.HasMenu ?? false,
          categoryId: apiItem.categoryId || apiItem.CategoryId || null,
          createdAt: apiItem.createdAt || apiItem.CreatedAt || '',
        } as InventoryItem));

        // Hariç tutulanlar (zaten ekli olanlar / ürünün kendisi / menüler)
        const filtered = mappedItems.filter((i) =>
          !excludeItemIds.includes(i.id) &&
          (!excludeMenuItems || !i.hasMenu)
        );

        setItems(filtered);
        setTotalCount(total);
      } catch (error) {
        console.error('❌ [ÜRÜN SEÇİM MODALI] Ürün yükleme hatası:', error);
        setItems([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, page, rowsPerPage, searchTerm, selectedWarehouseId, selectedCategoryId]);

  // Seçim işlemleri
  const handleToggleSelect = (item: InventoryItem) => {
    setSelectedMap(prev => {
      const next = new Map(prev);
      if (next.has(item.id)) {
        next.delete(item.id);
      } else {
        next.set(item.id, item);
      }
      return next;
    });
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedMap(prev => {
      const next = new Map(prev);
      if (event.target.checked) {
        items.forEach(item => next.set(item.id, item));
      } else {
        items.forEach(item => next.delete(item.id));
      }
      return next;
    });
  };

  const handleSaveSelection = () => {
    onSelectProducts(Array.from(selectedMap.values()));
    onClose();
  };

  const numSelectedOnPage = items.filter(item => selectedMap.has(item.id)).length;
  const rowCountOnPage = items.length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {title}
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {/* Filtreler */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              placeholder={tt('Ürün adı, stok kodu veya barkod ara...', 'Search product name, stock code or barcode...')}
              value={searchInput}
              onChange={(e) => handleSearchInputChange(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="inv-warehouse-filter-label">{tt('Depo', 'Warehouse')}</InputLabel>
              <Select
                labelId="inv-warehouse-filter-label"
                value={selectedWarehouseId || 'all'}
                onChange={(e: SelectChangeEvent<string>) => {
                  const value = e.target.value === 'all' ? null : e.target.value;
                  setSelectedWarehouseId(value);
                  setPage(0);
                }}
                label={tt('Depo', 'Warehouse')}
                startAdornment={
                  <InputAdornment position="start">
                    <WarehouseIcon fontSize="small" />
                  </InputAdornment>
                }
              >
                <MenuItem value="all">{tt('Tüm Depolar', 'All Warehouses')}</MenuItem>
                {warehouses.map(warehouse => (
                  <MenuItem key={warehouse.id} value={warehouse.id}>
                    {warehouse.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="inv-category-filter-label">{tt('Kategori', 'Category')}</InputLabel>
              <Select
                labelId="inv-category-filter-label"
                value={selectedCategoryId || 'all'}
                onChange={(e: SelectChangeEvent<string>) => {
                  const value = e.target.value === 'all' ? null : e.target.value;
                  setSelectedCategoryId(value);
                  setPage(0);
                }}
                label={tt('Kategori', 'Category')}
                startAdornment={
                  <InputAdornment position="start">
                    <CategoryIcon fontSize="small" />
                  </InputAdornment>
                }
              >
                <MenuItem value="all">{tt('Tüm Kategoriler', 'All Categories')}</MenuItem>
                {[...categories].sort((a, b) => a.name.localeCompare(b.name)).map(category => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {/* Ürün Tablosu */}
        <Box sx={{ position: 'relative', minHeight: 400 }}>
          {loading && (
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 10,
            }}>
              <CircularProgress />
            </Box>
          )}

          <TableContainer sx={{ maxHeight: 450 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      indeterminate={numSelectedOnPage > 0 && numSelectedOnPage < rowCountOnPage}
                      checked={rowCountOnPage > 0 && numSelectedOnPage === rowCountOnPage}
                      onChange={handleSelectAllClick}
                      inputProps={{ 'aria-label': 'select all items on this page' }}
                    />
                  </TableCell>
                  <TableCell>{tt('Ürün Adı', 'Product Name')}</TableCell>
                  <TableCell>{tt('Stok Kodu', 'Stock Code')}</TableCell>
                  <TableCell>{tt('Barkod', 'Barcode')}</TableCell>
                  <TableCell>{tt('Birim', 'Unit')}</TableCell>
                  <TableCell align="right">{tt('Alış Fiyatı', 'Purchase Price')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      {tt('Ürün bulunamadı', 'No products found')}
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item) => {
                    const isSelected = selectedMap.has(item.id);
                    const labelId = `inv-product-checkbox-${item.id}`;

                    return (
                      <TableRow
                        hover
                        onClick={() => handleToggleSelect(item)}
                        role="checkbox"
                        aria-checked={isSelected}
                        tabIndex={-1}
                        key={item.id}
                        selected={isSelected}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            color="primary"
                            checked={isSelected}
                            inputProps={{ 'aria-labelledby': labelId }}
                          />
                        </TableCell>
                        <TableCell component="th" id={labelId} scope="row">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {item.name}
                            {item.hasRecipe && (
                              <Chip
                                label={tt('Reçeteli', 'Has Recipe')}
                                size="small"
                                color="warning"
                                sx={{ height: 18, fontSize: '0.65rem' }}
                                icon={<RecipeIcon sx={{ fontSize: 12 }} />}
                              />
                            )}
                            {item.hasMenu && (
                              <Chip
                                label={tt('Menü', 'Menu')}
                                size="small"
                                color="info"
                                sx={{ height: 18, fontSize: '0.65rem' }}
                                icon={<MenuIcon sx={{ fontSize: 12 }} />}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>{item.stockCode || '-'}</TableCell>
                        <TableCell>{item.barcode || '-'}</TableCell>
                        <TableCell>{item.unit || '-'}</TableCell>
                        <TableCell align="right">
                          {item.purchasePrice
                            ? `${item.purchasePrice.toLocaleString(localeCode, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`
                            : '-'}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Sayfalama */}
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
          labelRowsPerPage={tt('Sayfa başına:', 'Rows per page:')}
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{tt('İptal', 'Cancel')}</Button>
        <Button onClick={handleSaveSelection} variant="contained" disabled={selectedMap.size === 0}>
          {tt(`Seçilenleri Ekle (${selectedMap.size})`, `Add Selected (${selectedMap.size})`)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InventoryProductSelectionModal;
